import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { User, UserRole, Student, Parent, CommunityMember, AdminUser } from '../types';
import { mockUsers, mockStudents, mockParents, mockCommunityMembers, mockAdminUsers, mockPassword } from '../mock';

interface AuthContextType {
  user: User | null;
  student: Student | null;
  parent: Parent | null;
  communityMember: CommunityMember | null;
  admin: AdminUser | null;
  login: (email: string, password: string, role: UserRole) => Promise<void>;
  logout: () => void;
  register: (parentData: Omit<Parent, 'id'>, password: string) => Promise<void>;
  registerCommunity: (communityData: Omit<CommunityMember, 'id'>, password: string) => Promise<void>;
  selectStudent: (studentId: string) => void;
  openStudentSelector: () => void;
  closeStudentSelector: () => void;
  isAuthenticated: boolean;
  needsStudentSelection: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [student, setStudent] = useState<Student | null>(null);
  const [parent, setParent] = useState<Parent | null>(null);
  const [communityMember, setCommunityMember] = useState<CommunityMember | null>(null);
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [needsStudentSelection, setNeedsStudentSelection] = useState(false);

  const login = useCallback(async (email: string, password: string, role: UserRole) => {
    if (role === 'admin') {
      const foundAdminUser = mockAdminUsers.find(a => a.email === email);
      if (!foundAdminUser) {
        throw new Error('Invalid email');
      }
      if (foundAdminUser.password !== password) {
        throw new Error('Invalid password');
      }
      if (!foundAdminUser.isActive) {
        throw new Error('Account is inactive');
      }
      const foundUser = mockUsers.find(u => u.email === email && u.role === role);
      if (!foundUser) {
        throw new Error('User not found');
      }
      setUser(foundUser);
      setAdmin(foundAdminUser);
      setNeedsStudentSelection(false);
      return;
    }

    if (password !== mockPassword) {
      throw new Error('Invalid password');
    }

    const foundUser = mockUsers.find(u => u.email === email && u.role === role);

    if (!foundUser) {
      throw new Error('Invalid email or role');
    }

    setUser(foundUser);

    if (role === 'student' && foundUser.studentId) {
      const foundStudent = mockStudents.find(s => s.id === foundUser.studentId);
      setStudent(foundStudent || null);
      setNeedsStudentSelection(false);
    } else if (role === 'parent' && foundUser.parentId) {
      const foundParent = mockParents.find(p => p.id === foundUser.parentId);
      setParent(foundParent || null);
      setNeedsStudentSelection(true);
    } else if (role === 'community' && foundUser.communityMemberId) {
      const foundCommunityMember = mockCommunityMembers.find(c => c.id === foundUser.communityMemberId);
      setCommunityMember(foundCommunityMember || null);
      setNeedsStudentSelection(false);
    }
  }, []);

  const register = useCallback(async (parentData: Omit<Parent, 'id'>, password: string) => {
    const newParentId = `650e8400-e29b-41d4-a716-${Date.now()}`;
    const newUserId = `750e8400-e29b-41d4-a716-${Date.now()}`;

    const newParent: Parent = {
      ...parentData,
      id: newParentId,
    };

    const newUser: User = {
      id: newUserId,
      email: parentData.email,
      role: 'parent',
      firstName: parentData.firstName,
      lastName: parentData.lastName,
      parentId: newParentId,
    };

    mockParents.push(newParent);
    mockUsers.push(newUser);

    setUser(newUser);
    setParent(newParent);
    setNeedsStudentSelection(true);
  }, []);

  const registerCommunity = useCallback(async (communityData: Omit<CommunityMember, 'id'>, password: string) => {
    const newCommunityMemberId = `850e8400-e29b-41d4-a716-${Date.now()}`;
    const newUserId = `950e8400-e29b-41d4-a716-${Date.now()}`;

    const newCommunityMember: CommunityMember = {
      ...communityData,
      id: newCommunityMemberId,
    };

    const newUser: User = {
      id: newUserId,
      email: communityData.email,
      role: 'community',
      firstName: communityData.firstName,
      lastName: communityData.lastName,
      communityMemberId: newCommunityMemberId,
    };

    mockCommunityMembers.push(newCommunityMember);
    mockUsers.push(newUser);

    setUser(newUser);
    setCommunityMember(newCommunityMember);
    setNeedsStudentSelection(false);
  }, []);

  const selectStudent = useCallback((studentId: string) => {
    const foundStudent = mockStudents.find(s => s.id === studentId);
    if (foundStudent) {
      setStudent(foundStudent);
      setNeedsStudentSelection(false);
    } else {
      throw new Error('Student not found');
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setStudent(null);
    setParent(null);
    setCommunityMember(null);
    setAdmin(null);
    setNeedsStudentSelection(false);
  }, []);

  const openStudentSelector = useCallback(() => {
    if (parent && parent.studentIds.length > 0) {
      setNeedsStudentSelection(true);
    }
  }, [parent]);

  const closeStudentSelector = useCallback(() => {
    setNeedsStudentSelection(false);
  }, []);

  const value: AuthContextType = {
    user,
    student,
    parent,
    communityMember,
    admin,
    login,
    logout,
    register,
    registerCommunity,
    selectStudent,
    openStudentSelector,
    closeStudentSelector,
    isAuthenticated: !!user,
    needsStudentSelection,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
